import {
  PlatformConfig,
  AdminBillingCustomer,
  AdminBillingMaker,
  AdminBillingOther,
  Billing,
  Home,
  Materials,
  NotFound,
  Order,
  Organisation,
  Organisations,
  PasswordRequest,
  PasswordReset,
  RFQView,
  RfqList,
  Playground,
  Signin,
  SigninError,
  Signup,
  OAuth,
  Tags,
  Users,
  WorkerJobsList,
  WorkerJobDetail,
  Workspace,
  Debug,
  DxfConversionTool,
  AcceptWithAuthCode,
  OfferResponse,
  PricingChangeResponse,
  CutlistUtils,
  PromoCodesAdmin,
  Checkout,
  AccountContactDetails,
  AccountSecurity,
  AccountNotifications,
  AccountOrganisationDetails,
  AccountAddresses,
  AccountMembers,
  AccountWrapper,
  TestData,
  UserWrapper,
  AdminSettings,
  ContactDetails,
  Hardware,
  AIToolsAdmin,
  Calls,
  CallDetails,
  DevTools,
  SentryTest,
} from '@/components/pages';
import { components } from '@/components/playground/playground';
import { Types, Roles } from './roles';
import { allTabs } from '@/components/elements/Order/navigation';
import { POSITION_TABS } from '@/components/elements/Positions/useUpdatePositionUrl';
import { stripStalePositionsQuery } from '@/components/elements/Positions/positionsRouteGuard';
import { isFeatureEnabled, waitForFlagsReady } from '@/helpers/unleash';
import SelectOrganisation from '@/components/pages/SelectOrganisation.vue';
import { buildWorkOSSigninUrl } from '@/helpers/workos-signin';
import {
  LEGACY_ORGANISATION_PATH,
  ORGANISATION_PATH,
  redirectLegacyOrganisationRoute,
} from './organisationRoutes';

const nav = [
  {
    name: 'home',
    title: 'NewQuote',
    show: ({ userStore }) =>
      !['Admin', 'Maker'].includes(userStore.activeSessionType) &&
      !userStore.userCutlistAdminSourceId,
  },
  {
    name: 'availableOrders',
    title: 'Available Orders',
    show: ({ userStore }) => !userStore.userCutlistAdminSourceId,
  },
  {
    name: 'workspace',
    title: 'Orders',
    show: ({ userStore }) => !userStore.userCutlistAdminSourceId,
  },
  {
    name: 'rfqs',
    title: 'RFQs',
    show: ({ userStore, isExternalAdmin }) =>
      !userStore.userCutlistAdminSourceId && !isExternalAdmin,
  },
  {
    name: 'users',
    title: 'Users',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'organisations',
    title: 'Organisations',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'materials',
    title: 'Materials',
    type: 'administration',
  },
  {
    name: 'hardwareAndAppliances',
    title: 'Hardware and Appliances',
    type: 'administration',
  },
  {
    name: 'promoCodes',
    title: 'Promo Codes',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'tags',
    title: 'Tags',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'billing',
    title: 'Billing',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'aiTools',
    title: 'AI Tools',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'calls',
    title: 'Calls',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'admin:config',
    title: 'Platform Config',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
  {
    name: 'workerJobs',
    title: 'Worker Jobs',
    type: 'administration',
    show: ({ isExternalAdmin }) => !isExternalAdmin,
  },
];

//  These routes (name) will force a logout on load.
const routeKillSession = {
  passwordrequest: true,
  passwordreset: true,
  demologin: true,
  signup: true,
};

const routes = [
  { path: '/', redirect: { name: 'home' } },

  {
    path: ORGANISATION_PATH,
    component: Organisation,
    meta: { title: 'Organisation', allowedTypes: Types.ADMIN_ONLY },
    children: [
      {
        path: '',
        name: 'organisations:details',
        component: Organisation,
        meta: { title: 'Organisation Details' },
      },
      {
        path: 'notifications',
        name: 'organisations:notifications',
        component: Organisation,
        meta: { title: 'Notifications' },
      },
      {
        path: 'addresses',
        name: 'organisations:addresses',
        component: Organisation,
        meta: { title: 'Addresses' },
      },
      {
        path: 'members',
        name: 'organisations:members',
        component: Organisation,
        meta: { title: 'Members' },
      },
      {
        path: 'order-preferences',
        name: 'organisations:orderPreferences',
        component: Organisation,
        meta: { title: 'Order Preferences' },
      },
    ],
  },
  {
    path: LEGACY_ORGANISATION_PATH,
    component: { render: () => null },
    meta: { title: 'Organisation', allowedTypes: Types.ADMIN_ONLY },
    beforeEnter: redirectLegacyOrganisationRoute,
  },
  {
    path: '/home',
    name: 'home',
    component: Home,
    meta: {
      title: 'Home',
      allowedTypes: Types.ALL,
    },
  },
  {
    path: '/signin',
    name: 'signin',
    component: Signin,
    meta: {
      title: 'Signin',
      allowedTypes: Types.NOT_LOGGED_IN,
    },
    beforeEnter: async (to) => {
      await waitForFlagsReady();
      const useWorkOS =
        to.query?.workos === 'true' || isFeatureEnabled('workos-auth');
      if (!useWorkOS) return true;

      const signinUrl = buildWorkOSSigninUrl(to.query);
      window.location.href = signinUrl.href;
      return false;
    },
  },
  {
    path: '/signin/error',
    name: 'signin:error',
    component: SigninError,
    meta: {
      title: 'Signin',
      allowedTypes: Types.NOT_LOGGED_IN,
    },
  },
  {
    path: '/signin/select-organisation',
    name: 'signin:selectOrganisation',
    component: SelectOrganisation,
    meta: {
      title: 'Signin',
      allowedTypes: new Set([Types.customer, Types.maker, Types.user]),
    },
  },
  {
    path: '/oauth',
    name: 'oauth',
    component: OAuth,
    meta: {
      title: 'OAuth',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/signup/:code?',
    name: 'signup',
    component: Signup,
    meta: {
      title: 'Sign up',
      allowedTypes: Types.NOT_LOGGED_IN,
    },
  },
  {
    path: '/passrequest',
    name: 'passwordrequest',
    component: PasswordRequest,
    meta: {
      title: 'Request a password reset',
      allowedTypes: Types.NOT_LOGGED_IN,
    },
  },
  {
    path: '/passreset/:code',
    name: 'passwordreset',
    component: PasswordReset,
    meta: {
      title: 'Reset your password',
      allowedTypes: Types.NOT_LOGGED_IN,
    },
  },
  {
    path: '/users',
    name: 'users',
    component: Users,
    meta: {
      title: 'Users',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/worker-jobs',
    name: 'workerJobs',
    component: WorkerJobsList,
    meta: {
      title: 'Worker Jobs',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/worker-jobs/:id',
    name: 'workerJobs:details',
    component: WorkerJobDetail,
    meta: {
      title: 'Worker Job',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },

  {
    path: '/users/:userId/view',
    redirect: { name: 'users:details' },
  },
  {
    path: '/users/:userId',
    component: UserWrapper,
    meta: {
      allowedTypes: Types.ADMIN_ONLY,
    },
    children: [
      {
        path: '',
        name: 'users:details',
        component: AdminSettings,
        meta: { title: 'User Details' },
      },
      {
        path: 'contact',
        name: 'users:contact',
        component: ContactDetails,
        meta: { title: 'Contact Details' },
      },
    ],
  },

  {
    path: '/neworder',
    redirect: '/home',
  },
  {
    path: '/order/:id',
    name: 'order',
    component: Order,
    meta: {
      deferPageTracking: true,
      disableAppChrome: true,
      allowedTypes: new Set([
        Types.admin,
        Types.maker,
        Types.customer,
        Types.unauthenticated,
      ]),
    },
    children: [
      ...allTabs
        .map((path) => ({
          path,
          name: `order:${path}`,
          component: Order,
          meta: { deferPageTracking: true, disableAppChrome: true },
        }))
        .filter(
          (tab) =>
            !['order:quotes', 'order:billing', 'order:positions'].includes(
              tab.name
            )
        ),
      {
        path: 'production',
        redirect: 'files',
      },
      {
        path: 'material-requests',
        redirect: { name: 'order:materials' },
      },
      {
        // Positions uses real nested routes.
        path: 'positions',
        name: 'order:positions',
        component: () =>
          import('@/components/elements/Positions/PositionsTab.vue'),
        meta: { deferPageTracking: true, disableAppChrome: true },
        beforeEnter: stripStalePositionsQuery,
        children: [
          {
            path: 'inbox',
            name: 'order:positions:inbox',
            component: () =>
              import('@/components/elements/Positions/OrderInbox.vue'),
            meta: { deferPageTracking: true, disableAppChrome: true },
          },
          {
            path: ':positionId/quote-file',
            name: 'order:positions:quote-file',
            component: () =>
              import('@/components/elements/Order/QuoteFile/QuoteFileTab.vue'),
            meta: {
              deferPageTracking: true,
              disableAppChrome: true,
              layout: 'fullscreen',
            },
          },
          {
            // Built from POSITION_TABS so the matcher can't drift from the
            // tabs the UI actually navigates to — a mismatch only surfaces on
            // a hard load, since named-route pushes skip the param regex.
            path: `:positionId/:tab(${POSITION_TABS.join('|')})?`,
            name: 'order:positions:position',
            component: () =>
              import('@/components/elements/Positions/PositionDetails.vue'),
            meta: { deferPageTracking: true, disableAppChrome: true },
          },
        ],
      },
      {
        path: 'quotes',
        name: 'order:quotes',
        components: { default: Order },
        meta: {
          allowedTypes: new Set([Types.customer, Types.admin, Types.maker]),
          allowedRoles: new Set([Roles.owner]),
          deferPageTracking: true,
          disableAppChrome: true,
        },
      },
      {
        path: 'billing',
        name: 'order:billing',
        components: { default: Order },
        meta: {
          allowedTypes: new Set([Types.customer, Types.admin, Types.maker]),
          allowedRoles: new Set([Roles.owner]),
          deferPageTracking: true,
          disableAppChrome: true,
        },
      },
    ],
  },
  {
    path: '/checkout/:orderId',
    name: 'checkout',
    component: Checkout,
    meta: {
      disableAppChrome: true,
      allowedTypes: new Set([Types.customer, Types.admin]),
      noBackground: true,
    },
  },
  {
    path: '/settings',
    component: AccountWrapper,
    meta: {
      allowedTypes: Types.LOGGED_IN,
    },
    children: [
      {
        path: '',
        name: 'account',
        component: AccountContactDetails,
        meta: { title: 'Contact Details' },
      },
      {
        path: 'security',
        name: 'account:security',
        component: AccountSecurity,
        meta: { title: 'Password' },
      },
      {
        path: 'organisation',
        name: 'account:organisation',
        component: AccountOrganisationDetails,
        meta: { title: 'Organisation Details' },
      },
      {
        path: 'notifications',
        name: 'account:notifications',
        component: AccountNotifications,
        meta: { title: 'Notifications' },
      },
      {
        path: 'addresses',
        name: 'account:addresses',
        component: AccountAddresses,
        meta: { title: 'Address Book' },
      },
      {
        path: 'members',
        name: 'account:members',
        component: AccountMembers,
        meta: { title: 'Members' },
      },
    ],
  },
  {
    path: '/account',
    children: [
      {
        path: '',
        name: 'settings',
        redirect: { name: 'account' },
      },
      {
        path: 'addresses',
        name: 'settings:addresses',
        redirect: { name: 'account:addresses' },
      },
      {
        path: 'notifications',
        name: 'settings:notifications',
        redirect: { name: 'account:notifications' },
      },
      {
        path: 'locale',
        name: 'settings:locale',
        redirect: { name: 'account' },
      },
      {
        path: 'test-data',
        name: 'settings:testData',
        redirect: { name: 'account' },
      },
    ],
  },
  {
    path: '/dev-tools',
    component: DevTools,
    meta: { allowedTypes: Types.ADMIN_ONLY },
    children: [
      {
        path: '',
        redirect: { name: 'devTools:testData' },
      },
      {
        path: 'test-data',
        name: 'devTools:testData',
        component: TestData,
        meta: { title: 'Test Data' },
      },
      {
        path: 'sentry-test',
        name: 'devTools:sentryTest',
        component: SentryTest,
        meta: { title: 'Sentry Test' },
      },
    ],
  },
  { path: '/test-data', redirect: { name: 'devTools:testData' } },

  {
    path: '/debug/:id',
    name: 'debug',
    component: Debug,
    meta: {
      allowedTypes: new Set([Types.admin]),
    },
  },
  {
    path: '/dev/cutlist-utils',
    name: 'dev:cutlist-utils',
    component: CutlistUtils,
    meta: {
      title: 'Cutlist utils',
      allowedTypes: new Set([Types.admin]),
    },
  },
  {
    path: '/admin/config/:group?',
    name: 'admin:config',
    component: PlatformConfig,
    meta: {
      title: 'Platform Config',
      allowedTypes: new Set([Types.admin]),
    },
  },
  {
    path: '/materials',
    name: 'materials',
    component: Materials,
    meta: {
      title: 'Materials',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/hardware-and-appliances',
    name: 'hardwareAndAppliances',
    component: Hardware,
    meta: {
      title: 'Hardware and Appliances',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/promo-codes',
    name: 'promoCodes',
    component: PromoCodesAdmin,
    meta: {
      title: 'Promo Codes',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/tags',
    name: 'tags',
    component: Tags,
    meta: {
      title: 'Tags',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/ai-tools',
    name: 'aiTools',
    component: AIToolsAdmin,
    meta: {
      title: 'AI Tools',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/calls',
    name: 'calls',
    component: Calls,
    meta: {
      title: 'Calls',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/calls/:id',
    name: 'calls:details',
    component: CallDetails,
    meta: {
      title: 'Call Details',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
  {
    path: '/billing',
    name: 'billing',
    component: Billing,
    meta: {
      allowedTypes: new Set([Types.admin, Types.maker, Types.customer]),
      allowedRoles: new Set([Roles.owner]),
    },
    children: [
      {
        path: 'customer',
        name: 'billing:customer',
        component: AdminBillingCustomer,
        meta: {
          allowedTypes: Types.ADMIN_ONLY,
          title: 'Customer Invoices',
          subNav: true,
        },
      },
      {
        path: 'maker',
        name: 'billing:maker',
        component: AdminBillingMaker,
        meta: {
          allowedTypes: Types.ADMIN_ONLY,
          allowedRoles: new Set([Roles.owner]),
          title: 'Maker Invoices',
          subNav: true,
        },
      },
      {
        path: 'other',
        name: 'billing:other',
        component: AdminBillingOther,
        meta: {
          allowedTypes: Types.ADMIN_ONLY,
          title: 'Third Party Invoices',
          subNav: true,
        },
      },
    ],
  },
  {
    path: '/available-orders',
    name: 'availableOrders',
    component: Workspace,
    meta: {
      title: 'Available Orders',
      allowedTypes: new Set([Types.maker]),
    },
  },
  {
    path: '/workspace',
    name: 'workspace',
    component: Workspace,
    meta: {
      title: 'Orders',
      allowedTypes: new Set([Types.admin, Types.maker, Types.customer]),
    },
  },
  {
    path: '/notfound',
    name: 'notfound',
    component: NotFound,
    meta: {
      title: 'Page not found',
      allowedTypes: Types.ALL,
    },
  },
  {
    path: '/rfq/:id',
    name: 'rfq',
    component: RFQView,
    meta: {
      title: 'RFQ',
      allowedTypes: new Set([Types.admin, Types.maker, Types.customer]),
      allowedRoles: new Set([Roles.owner]),
      noBackground: true,
    },
  },
  {
    path: '/rfqs',
    name: 'rfqs',
    component: RfqList,
    meta: {
      title: 'RFQs',
      allowedTypes: new Set([Types.admin, Types.maker]),
      allowedRoles: new Set([Roles.owner]),
    },
  },
  {
    path: '/playground',
    name: 'playground',
    component: Playground,
    meta: {
      title: 'Playground',
      allowedTypes: Types.ADMIN_ONLY,
      noBackground: true,
    },
    children: components,
  },
  {
    path: '/dxf-conversion-tool',
    name: 'dxfConversionTool',
    component: DxfConversionTool,
    meta: {
      title: 'dxfConversionTool',
      whiteLabel: true,
      allowedTypes: Types.ALL,
    },
  },
  {
    path: '/:pricingType/:id/accept/:code',
    name: 'acceptPricing',
    component: AcceptWithAuthCode,
    meta: {
      allowedTypes: Types.ALL,
    },
  },
  {
    path: '/offer-response',
    name: 'offerResponse',
    component: OfferResponse,
    meta: {
      allowedTypes: Types.ALL,
    },
  },
  {
    path: '/pricing-change/:id/respond/:token',
    name: 'pricingChangeResponse',
    component: PricingChangeResponse,
    meta: {
      allowedTypes: Types.ALL,
    },
  },

  { path: '/maker-organisations', redirect: { name: 'organisations' } },
  { path: '/customer-organisations', redirect: { name: 'organisations' } },
  {
    path: '/organisations',
    name: 'organisations',
    component: Organisations,
    meta: {
      title: 'Organisations',
      allowedTypes: Types.ADMIN_ONLY,
    },
  },
];

const routeIsAllowed = function (userType, userRole, target) {
  const route = [...target.matched]
    .reverse()
    .find((route) => route.meta?.allowedTypes);

  const allowedTypes = route?.meta?.allowedTypes;
  const allowedRoles = route?.meta?.allowedRoles;

  const typeAllowed = allowedTypes ? allowedTypes.has(userType) : false;
  const roleAllowed = allowedRoles ? allowedRoles.has(userRole) : true;

  return typeAllowed && roleAllowed;
};

const handleRoute = function (type, role, target) {
  if (routeIsAllowed(type, role, target)) {
    if (type !== 'Public' && target.query.loginParams) {
      // strip loginParams from the query before navigating to the target path
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loginParams: _, ...query } = target.query;
      return { path: target.path, query, hash: target.hash };
    }
    return true;
  }
  if (type === 'Public') {
    return {
      name: 'signin',
      query: {
        redirect: target.query.redirect || target.fullPath,
        loginParams: target.query.loginParams,
      },
    };
  }
  if (type === 'User') {
    return {
      name: 'signin:selectOrganisation',
      query: { redirect: target.query.redirect || target.fullPath },
    };
  }
  return { name: 'home' };
};

const pathIsAllowed = function (userType, userRole, pathName) {
  const route = routes.find((r) => r.name === pathName);
  if (!route) {
    return false;
  }
  const allowedTypes = route?.meta?.allowedTypes;
  const allowedRoles = route?.meta?.allowedRoles;

  const typeAllowed = allowedTypes ? allowedTypes.has(userType) : false;
  if (!typeAllowed) return false;

  const roleAllowed = allowedRoles ? allowedRoles.has(userRole) : true;
  if (!roleAllowed) return false;

  return true;
};

export { routes, nav, routeKillSession, pathIsAllowed, handleRoute };
